import { FC, memo, useCallback, useState } from 'react'

import { ReportFile } from 'commons/types/DTO/bank'
import { UploadedFile } from 'commons/types/DTO/bank/reportWebPage'
import { TableDataQa } from 'commons/types/enums'
import { Layer } from 'ui/components/Layer'
import { PageSectionPrimary } from 'ui/components/Page'
import { Table } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { UploadedFilesTableProps } from './UploadedFilesTableProps'
import { columns } from './columns'
import { UploadButton } from './components/UploadButton/UploadButton'
import { useDeleteFile } from './hooks/useDeleteFile'
import { useUploadFileCreate } from './hooks/useUploadFileRequest'

export const UploadedFilesTable: FC<UploadedFilesTableProps> = memo((props) => {
  const { webPage, invalidateMR } = props
  const { createFile, isPending: isFileUploading } = useUploadFileCreate(webPage.monthlyAnalyticsId, invalidateMR)
  const { deleteFile, isPending: isFileDeleting } = useDeleteFile(webPage.monthlyAnalyticsId, invalidateMR)
  const [fileToDelete, setFileToDelete] = useState<number>()

  const { Confirmation: FileDeleteConfirmation, open: fileDeleteConfirmationOpen } = useConfirmation({
    message: `Are you sure you want to delete this file?`,
    onConfirm: () => (fileToDelete ? deleteFile({ id: fileToDelete }) : null),
    onCancel: () => setFileToDelete(undefined),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const onFilesUpload = useCallback(
    async (files: File[]) => {
      const filesPromises: Promise<ReportFile>[] = []
      files.forEach((file) => {
        filesPromises.push(
          createFile({
            file,
            reportMonthlyId: webPage.monthlyAnalyticsId ?? 0
          })
        )
      })
      await Promise.all(filesPromises)
    },
    [createFile, webPage.monthlyAnalyticsId]
  )

  const onFileDelete = useCallback(
    (id: number) => {
      setFileToDelete(id)
      fileDeleteConfirmationOpen()
    },
    [setFileToDelete, fileDeleteConfirmationOpen]
  )

  return (
    <PageSectionPrimary
      face="outlined"
      title="Uploaded Files"
      anchor="uploaded_files"
      actions={<UploadButton onUpload={onFilesUpload} />}
    >
      <Layer shadowed rounded>
        <Table<UploadedFile>
          dataQa={TableDataQa.BP_MONTHLY_ANALYTICS_UPLOADED_FILES}
          loading={isFileDeleting || isFileUploading}
          dataSource={webPage.uploadedFiles.map((el) => ({
            item: el
          }))}
          columns={columns(onFileDelete)()}
        />
        <FileDeleteConfirmation />
      </Layer>
    </PageSectionPrimary>
  )
})
